import { Box, Tooltip, Typography } from "@mui/material";
import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";

import { chartPalette, colors } from "../../theme/tokens";
import type { WidgetProps } from "../types";
import { Card, CardBody, CardFigure, CardLabel, SourceLine } from "./card";
import { formatGeneratedAt } from "./format";

// Opportunity-page KPI strip. A row of tiles, any of which can carry a gauge
// (a share, a coverage ratio, a 0-100 quality score) instead of a figure.
//
// The null state is the reason this widget exists rather than a rich_text
// table. `value: null` renders "Not available" plus the source that would fill
// it, so a number we could not get shows as a named gap instead of quietly
// disappearing or, worse, being guessed. Every tile carries its source, shown
// on hover and printed under the strip.
export function MetricTiles({ widget }: WidgetProps<"metric_tiles">) {
    const { title, description } = widget.props;
    const { tiles, generatedAt } = widget.data;

    return (
        <Box>
            {title && (
                <Typography variant="h4" sx={{ mb: description ? 0.5 : 1 }}>
                    {title}
                </Typography>
            )}
            {description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 760 }}>
                    {description}
                </Typography>
            )}
            {/* Opt-outs for a pitch page (exclusivity room, Elijah 2026-10-27): the snapshot
                line and the per-tile sources read as clutter there. The sources stay
                required in the data; only their display is off. */}
            {!widget.props.hideSnapshot && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 2 }}
                >
                    Snapshot · {formatGeneratedAt(generatedAt)}
                </Typography>
            )}

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: `repeat(${widget.props.columns ?? 3}, 1fr)`,
                    },
                    gap: 2,
                }}
            >
                {tiles.map((t) => (
                    <Tile key={t.label} tile={t} hideSource={widget.props.hideSources} />
                ))}
            </Box>
        </Box>
    );
}

type Tile = WidgetProps<"metric_tiles">["widget"]["data"]["tiles"][number];

function Tile({ tile, hideSource }: { tile: Tile; hideSource?: boolean }) {
    const available = tile.value !== null;

    return (
        <Card sx={{ display: "flex", flexDirection: "column" }}>
            {/* An opt-in accent colours the label only, from chartPalette (no accent borders). */}
            <CardLabel sx={tile.accent != null ? { color: chartPalette[tile.accent] } : undefined}>
                {tile.label}
            </CardLabel>

            {tile.gauge && available ? (
                <Box sx={{ alignSelf: "flex-start", mt: 1 }}>
                    <Gauge
                        width={116}
                        height={92}
                        value={tile.gauge.value}
                        valueMax={tile.gauge.max}
                        startAngle={-110}
                        endAngle={110}
                        text={tile.gauge.valueLabel ?? tile.value ?? ""}
                        sx={{
                            [`& .${gaugeClasses.valueText}`]: {
                                fontSize: 20,
                                fontWeight: 800,
                                fill: colors.navy,
                            },
                            [`& .${gaugeClasses.valueArc}`]: { fill: chartPalette[0] },
                            [`& .${gaugeClasses.referenceArc}`]: { fill: colors.lightGray },
                        }}
                    />
                </Box>
            ) : (
                <CardFigure size={available ? 36 : 18} muted={!available}>
                    {available ? tile.value : "Not available"}
                </CardFigure>
            )}

            {tile.caption && <CardBody>{tile.caption}</CardBody>}

            {!available && tile.sourceNote && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1, fontSize: "0.8125rem", fontStyle: "italic" }}
                >
                    {tile.sourceNote}
                </Typography>
            )}

            {!hideSource && (
                <Tooltip title={tile.source}>
                    <Box sx={{ mt: "auto" }}>
                        <SourceLine
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {tile.source}
                        </SourceLine>
                    </Box>
                </Tooltip>
            )}
        </Card>
    );
}
